import { getUserState } from '@carvana/core-auth';
import get from 'lodash.get';
import isEqual from 'lodash.isequal';
import { createSelector } from 'reselect';
import { Address } from '../../../contracts';
import type { RootState } from '../../../store/types';

export const getPersonalDetails = (state: RootState) => state.personalDetails;

export const getUpdateContactsDetailsIsLoading = (state: RootState) =>
  state.personalDetails.updateContactsDetails.isLoading;

export const getUpdateContactsDetailsError = (state: RootState) =>
  state.personalDetails.updateContactsDetails.error;

export const getUpdateAddressIsLoading = (state: RootState) =>
  state.personalDetails.updateAddress.isLoading;

export const getUpdatePersonalInformationIsLoading = (state: RootState) =>
  state.personalDetails.updatePersonalInformation.isLoading;

export const getUpdatePasswordIsLoading = (state: RootState) =>
  state.personalDetails.updatePassword.isLoading;

export const getUserProfileData = (state: RootState) => state.personalDetails.userProfileData;

export const getUserId = createSelector(getPersonalDetails, (personalDetails) => {
  const { user } = getUserState();
  return user.user_id;
});

export const getFirstName = createSelector(getPersonalDetails, (personalDetails) => {
  const firstName = get(personalDetails, 'entities.name.first', '');
  return firstName;
});

export const getMiddleName = createSelector(getPersonalDetails, (personalDetails) => {
  const middleName = get(personalDetails, 'entities.name.middle', '');
  return middleName;
});

export const getLastName = createSelector(getPersonalDetails, (personalDetails) => {
  const lastName = get(personalDetails, 'entities.name.last', '');
  return lastName;
});

export const getSuffix = createSelector(getPersonalDetails, (personalDetails) => {
  const suffix = get(personalDetails, 'entities.name.suffix', '');
  return suffix;
});

export const getFullName = createSelector(
  [getFirstName, getMiddleName, getLastName, getSuffix],
  (firstName, middleName, lastName, suffix) => {
    const isFullNameAvailable = firstName || lastName;
    const middleNameString = middleName ? ` ${middleName} ` : ' ';
    const suffixString = suffix ? ` ${suffix}` : '';
    const fullName = `${firstName}${middleNameString}${lastName}${suffixString}`;
    const defaultName = '';
    const name = isFullNameAvailable ? fullName : defaultName;

    return name;
  }
);

export const getPhoneNumber = createSelector(getPersonalDetails, (personalDetails) => {
  const phoneNumber = get(personalDetails, 'entities.phoneNumber', '');
  const formattedPhoneNumber = phoneNumber.replace(/[-,(,), ]/g, '');
  return formattedPhoneNumber;
});

export const getEmailAddress = createSelector(getPersonalDetails, (personalDetails) => {
  const emailAddress = get(personalDetails, 'entities.emailAddress', '');

  return emailAddress;
});

export const getDateOfBirth = createSelector(getPersonalDetails, (personalDetails) => {
  const dateOfBirth = get(personalDetails, 'entities.dateOfBirth', '');
  return dateOfBirth;
});

export const getIncome = createSelector(getPersonalDetails, (personalDetails) => {
  const income = get(personalDetails, 'entities.income', '');
  return income;
});

export const getShouldShowSsn = createSelector(getPersonalDetails, (personalDetails) => {
  const shouldShowSsn = get(personalDetails, 'entities.hasSsn', false);
  return shouldShowSsn;
});

export const getShouldShowPersonalInfoSection = createSelector(
  [getFullName, getPhoneNumber, getEmailAddress],
  (fullName, phoneNumber, emailAddress) => {
    return Boolean(fullName || phoneNumber || emailAddress);
  }
);

export const getHomeAddress = createSelector(getPersonalDetails, (personalDetails) => {
  const addresses = get(personalDetails, 'entities.addresses', null);
  const homeAddressKinds = ['VerifiedHome', 'UnverifiedHome'];
  if (!addresses) return null;

  return addresses.find((address) => homeAddressKinds.includes(address.kind)) ?? null;
});

export const getMailingAddress = createSelector(getPersonalDetails, (personalDetails) => {
  const addresses = get(personalDetails, 'entities.addresses', null);
  const mailingAddressKinds = ['VerifiedMailing'];
  if (!addresses) return null;
  const existingMailingAddress = addresses.find((address) =>
    mailingAddressKinds.includes(address.kind)
  );
  const emptyMailingAddress: Address = {
    kind: 'VerifiedMailing',
    address: {
      street: '',
      street2: '',
      city: '',
      state: '',
      zipCode: {
        zip5: ''
      }
    }
  };
  const mailingAddress = !existingMailingAddress ? emptyMailingAddress : existingMailingAddress;
  return mailingAddress;
});

export const getDeliveryAddress = createSelector(getPersonalDetails, (personalDetails) => {
  const addresses = get(personalDetails, 'entities.addresses', null);
  const deliveryAddressKinds = ['VerifiedDelivery', 'DeliveryZip'];
  if (!addresses) return null;

  return addresses.find((address) => deliveryAddressKinds.includes(address.kind)) ?? null;
});

export const getShouldShowHomeAddress = createSelector(getHomeAddress, (homeAddress) =>
  Boolean(homeAddress)
);
export const getShouldShowMailingAddress = createSelector(getHomeAddress, (homeAddress) =>
  Boolean(homeAddress)
);
export const getShouldShowDeliveryAddress = createSelector(getDeliveryAddress, (deliveryAddress) =>
  Boolean(deliveryAddress)
);

export const getIsMailingAddressSameAsHomeAddress = createSelector(
  [getHomeAddress, getMailingAddress],
  (homeAddress, mailingAddress) => {
    const homeAddressObject = get(homeAddress, 'address', null);
    const mailingAddressObject = get(mailingAddress, 'address', null);
    const shouldDeleteTimeStamp = homeAddressObject && mailingAddressObject;

    if (shouldDeleteTimeStamp) {
      delete homeAddressObject.lastUpdatedDateTime;
      delete mailingAddressObject.lastUpdatedDateTime;
    }

    return isEqual(homeAddressObject, mailingAddressObject);
  }
);

export const getIsDeliveryAddressSameAsHomeAddress = createSelector(
  [getHomeAddress, getDeliveryAddress],
  (homeAddress, deliveryAddress) => {
    const homeAddressObject = get(homeAddress, 'address', null);
    const deliveryAddressObject = get(deliveryAddress, 'address', null);
    const shouldDeleteTimeStamp = homeAddressObject && deliveryAddressObject;

    if (shouldDeleteTimeStamp) {
      delete homeAddressObject.lastUpdatedDateTime;
      delete deliveryAddressObject.lastUpdatedDateTime;
    }

    return isEqual(homeAddressObject, deliveryAddressObject);
  }
);

export const getIsLoadingPersonalDetails = createSelector(getPersonalDetails, (personalDetails) => {
  const isLoadingPersonalDetails = get(personalDetails, 'showPersonalDetails.isLoading', true);
  return isLoadingPersonalDetails;
});

export const getPersonalDetailsError = createSelector(getPersonalDetails, (personalDetails) => {
  const personalDetailsError = get(personalDetails, 'showPersonalDetails.error');
  return personalDetailsError;
});

export const getCanEditPersonalDetails = createSelector(getPersonalDetails, (personalDetails) => {
  const canEditPersonalDetails = get(personalDetails, 'entities.canEditPersonalDetails', false);

  return canEditPersonalDetails;
});

export const getIsOrderPlaced = createSelector(getPersonalDetails, (personalDetails) => {
  const isOrderPlaced = get(personalDetails, 'entities.isOrderPlaced', false);
  return isOrderPlaced;
});

export const getIsOrderInProgress = createSelector(getPersonalDetails, (personalDetails) => {
  const purchases = get(personalDetails, 'purchases');
  if (!purchases || !purchases.data) return false;
  // Check if any purchase has the status 'orderInProgress'
  const isOrderInProgress = purchases.data?.some((purchase) => {
    return purchase.status === 'orderInProgress';
  });
  return isOrderInProgress;
});

export const getIsIn7DMBG = createSelector(getPersonalDetails, (personalDetails) => {
  const purchases = get(personalDetails, 'purchases');
  if (!purchases || !purchases.data) return false;
  // Check if any post-purchase vehicle sale date is within 7 days from now

  const isIn7DMBG = purchases.data
    ?.filter((purchase) => purchase.status === 'postPurchase')
    .some((postPurchase) => {
      if (!postPurchase.vehicle?.saleDate) return false;

      const lastDay = new Date(postPurchase.vehicle?.saleDate);
      lastDay.setDate(lastDay.getDate() + 7);
      return new Date(Date.now()) < lastDay;
    });
  return isIn7DMBG;
});

export const getMayEditPersonalDetails = createSelector(
  [getCanEditPersonalDetails, getIsOrderPlaced, getIsOrderInProgress, getIsIn7DMBG],
  (canEditPersonalDetails, isOrderPlaced, isOrderInProgress, isIn7DMBG) => {
    return canEditPersonalDetails && !isOrderPlaced && !isOrderInProgress && !isIn7DMBG;
  }
);

export const getIsRandomPassword = createSelector(
  getUserProfileData,
  (profileData) => !!profileData?.userProfile?.accountFlags?.isAutoGeneratedPassword
);

export const getPreferredName = createSelector(
  getUserProfileData,
  (profileData) => profileData?.userProfile?.userPreferences?.preferredName || null
);

export const getPreferredLanguage = createSelector(
  getUserProfileData,
  (profileData) => profileData?.userProfile?.userPreferences?.preferredLanguage || null
);

export const getLanguages = createSelector(
  getUserProfileData,
  (profileData) => profileData?.getLanguages?.languages?.map((lang) => lang.nativeName) || []
);
